import React, {useMemo, useState} from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { Button } from "@/components/ui/Button";
import { ChevronDownIcon } from "lucide-react";
import { Calendar } from "@/components/ui/Calendar";
import { formatDate } from "date-fns";
import { days } from "@/constants/common";
import { Service } from "../ServiceCard";
import { useGetServiceAvailibilityQuery } from "@/services/LiveQueueService";

interface TicketAvailabilityCalendarProps {
  value: string;
  onChange: (value: string) => void,
  service: Service | null
}

const TicketAvailabilityCalendar = ({value, onChange, service}: TicketAvailabilityCalendarProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [month,setMonth] = useState<Date>(new Date());
  const { data: ticketAvailability, } = useGetServiceAvailibilityQuery({
    service_id: service?.id as number,
    start_date: month?formatDate(new Date((month as Date).getFullYear(), (month as Date).getMonth(), 1), 'yyyy-MM-dd'):'',
    end_date: month?formatDate(new Date((month as Date).getFullYear(), (month as Date).getMonth() + 1, 0), 'yyyy-MM-dd'):'',
  }, {
    skip: !service || !month
  });

  const disabledDates = useMemo(() => {
    const disabledBeforeToday = { before: new Date() };
    const allowedDays = service?.schedule.map(s => s.day) ?? [];
    const disabledDaysOfWeek = {
      dayOfWeek: days
        .map((day, index) => ({ day, index }))
        .filter(({ day }) => !allowedDays.includes(day))
        .map(({ index }) => index)
    };
    const disabledUnavailableDates = ticketAvailability
      ? Object.keys(ticketAvailability)
          .filter(date => !ticketAvailability[date])
          .map(date => new Date(date))
      : [];

    return [
      disabledBeforeToday,
      disabledDaysOfWeek,
      ...disabledUnavailableDates
    ];
  }, [service, ticketAvailability]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          id="date"
          className={
            `w-full justify-between font-normal border-checkboxborder border-[1px] text-gray-500 bg-white outline-none bg-clip-border
            ${open && "ring-2 ring-primary border-none ring-offset-1"}`
          }
        >
          {value ? new Date(value).toLocaleDateString() : "Select date"}
          <ChevronDownIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          selected={new Date(value)}
          captionLayout="dropdown"
          onSelect={(date) => {
            onChange(formatDate(date??"", 'yyyy-MM-dd'))
            setOpen(false)
          }}
          disabled={disabledDates}		
          onMonthChange={monthDate => setMonth(monthDate)}
        />
      </PopoverContent>
    </Popover>
  );
}

export default TicketAvailabilityCalendar;
